import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  DIRECT_RIDE_STATIONS_COUNT,
  durationInMinutesCeil,
  durationInMinutesFloor,
  editFormDataAF,
  getEffectiveMaxRideDurationMinutes,
  NO_VALUE_DISPLAY,
  RideCrudModeKeyTuple,
  systemSettingsDtoAtom,
  type IRideDto,
} from "@ryde/shared";

import { DurationLabelExceeded } from "./duration-label-exceeded";
import { DurationLabelNormal } from "./duration-label-normal";

export const RideTotalDurationLabel: FC = () => {
  const { t } = useTranslation();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const totalDurationMinutesFloor = durationInMinutesFloor(
    rideDto?.metadata?.totalDuration ?? null,
  );
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);

  const effectiveMaxDurationInMinutes = useMemo(() => {
    return getEffectiveMaxRideDurationMinutes({
      priceRateMaxDurationInMinutes: rideDto?.priceRateMaxDurationInMinutes,
      systemParams: systemSettingsDto?.metadata,
      direction: rideDto?.direction,
    });
  }, [
    rideDto?.direction,
    rideDto?.priceRateMaxDurationInMinutes,
    systemSettingsDto?.metadata,
  ]);

  const isTotalDurationExceeded = useMemo(() => {
    return (
      !!totalDurationMinutesFloor &&
      totalDurationMinutesFloor > 0 &&
      !!effectiveMaxDurationInMinutes &&
      effectiveMaxDurationInMinutes > 0 &&
      totalDurationMinutesFloor > effectiveMaxDurationInMinutes
    );
  }, [effectiveMaxDurationInMinutes, totalDurationMinutesFloor]);

  const isRideOvertimeOnProduceAllowed = useMemo(() => {
    return !!(
      systemSettingsDto?.metadata
        ?.ignoreTimeLimitsForsavedMLRouteUsedForReorders &&
      rideDto?.productionId
    );
  }, [
    rideDto?.productionId,
    systemSettingsDto?.metadata?.ignoreTimeLimitsForsavedMLRouteUsedForReorders,
  ]);

  const isDirectRide = useMemo(() => {
    const stationsCount =
      rideDto?.metadata?.stationsInfo?.length ||
      rideDto?.metadata?.stationIds?.length ||
      0;
    return stationsCount === DIRECT_RIDE_STATIONS_COUNT;
  }, [
    rideDto?.metadata?.stationsInfo?.length,
    rideDto?.metadata?.stationIds?.length,
  ]);

  const isIgnoredMaxDurationForDirectRide = useMemo(() => {
    return !!(
      systemSettingsDto?.metadata?.ignoreMaxDurationForDirectRide &&
      isDirectRide
    );
  }, [
    systemSettingsDto?.metadata?.ignoreMaxDurationForDirectRide,
    isDirectRide,
  ]);

  const totalRideDurationDisplayValue =
    durationInMinutesCeil(rideDto?.metadata?.totalDuration) || NO_VALUE_DISPLAY;
  const priceRateMaxDurationDisplayValue =
    effectiveMaxDurationInMinutes ?? NO_VALUE_DISPLAY;

  const displayContent = rideDto?.metadata?.totalDuration
    ? `${totalRideDurationDisplayValue} ${t(`general.MIN`)}`
    : NO_VALUE_DISPLAY;

  if (isTotalDurationExceeded) {
    return (
      <DurationLabelExceeded
        totalRideDurationDisplayValue={totalRideDurationDisplayValue}
        priceRateMaxDurationDisplayValue={priceRateMaxDurationDisplayValue}
        isRideOvertimeOnProduceAllowed={isRideOvertimeOnProduceAllowed}
        isIgnoredMaxDurationForDirectRide={isIgnoredMaxDurationForDirectRide}
        displayContent={displayContent}
      />
    );
  }

  return <DurationLabelNormal displayContent={displayContent} />;
};
